/**
 * Rahmen für die shell-freien Routen (#504).
 *
 * Trägt die Icon-Rail — den einzigen Ort, an dem im Standalone-Betrieb
 * navigiert wird: zwischen den Apps (Chat, Drive), zurück in den WebOS-Desktop,
 * und ins Nutzer-Menü zum Abmelden. Bewusst als Rail statt als Kopfleiste: der
 * Chat lebt vertikal, und 56 px Breite kosten ihn nichts.
 *
 * Das Layout stellt außerdem den Standalone-`AppHost` bereit — einmal für alle
 * Routen darunter, statt in jeder Seite erneut.
 *
 * Seit #1169 trägt die Rail auch die Glocke (dasselbe Benachrichtigungscenter
 * wie im Desktop, hier links unten neben der Rail) und „Meine Tickets"
 * (`/tickets`). Benachrichtigungen öffnen ihr Ziel hier als Route statt als
 * Fenster (`NotificationHostProvider`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { HardDrive, LayoutGrid, LogOut, MessageSquare, Settings, Ticket, TicketPlus } from 'lucide-react'
import { Tooltip, TooltipProvider } from '@/components/ui/tooltip'
import { StandaloneTicketPanel } from '@/components/system/ticket-panel/StandaloneTicketPanel'
import { NotificationBell } from '@/components/system/NotificationBell'
import { NotificationHostProvider, TICKETS_ROUTE } from '@/components/system/notifications/notificationHost'
import { ACCENT_FILL } from '@/components/system/notifications/notificationStyles'
import { OPEN_BUG_REPORT_EVENT } from '@/services/bug-report-service'
import { clearAllowedTicketTypesCache, useAllowedTicketTypes } from '@/hooks/useAllowedTicketTypes'
import { useBugReportUnreadCount } from '@/hooks/useBugReportUnreadCount'
import { authService } from '@/services/auth-service'
import { AppHostProvider, useStandaloneAppHost, STANDALONE_ROUTES } from '@/hooks/useAppHost'
import {
  useAllowedApplications,
  clearAllowedApplicationsCache,
  DESKTOP_APPLICATION,
} from '@/hooks/useAllowedApplications'
import { logError } from '@/lib/logger'
import { env } from '@/lib/env'
import type { AppId } from '@/data/apps'

/** Die Apps der Rail, in Anzeigereihenfolge. Routen kommen aus der einen Tabelle. */
const RAIL_APPS: { id: AppId; Icon: typeof MessageSquare }[] = [
  { id: 'communicator2', Icon: MessageSquare },
  { id: 'drive2', Icon: HardDrive },
]

/** Initialen für den Avatar — zwei Buchstaben, sonst der erste des Namens. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function RailButton({
  label,
  active,
  onClick,
  to,
  badge,
  testId,
  children,
}: {
  label: string
  active?: boolean
  onClick?: () => void
  to?: string
  /** A count in the corner (e.g. tickets with new replies); 0 shows nothing. */
  badge?: number
  testId?: string
  children: React.ReactNode
}) {
  const className = [
    'relative grid h-11 w-11 place-items-center rounded-xl transition-colors',
    'text-[var(--text-secondary)] hover:bg-[var(--sidebar-hover)] hover:text-[var(--text-color)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    active ? 'bg-[var(--sidebar-hover)] text-[var(--text-color)]' : '',
  ].join(' ')

  const badgeElement =
    badge && badge > 0 ? (
      <span
        aria-hidden
        data-testid={testId ? `${testId}-badge` : undefined}
        className="absolute right-0.5 top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[11px] font-semibold"
        style={ACCENT_FILL}
      >
        {badge > 99 ? '99+' : badge}
      </span>
    ) : null

  const content = to ? (
    <Link
      to={to}
      className={className}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      data-testid={testId}
    >
      {children}
      {badgeElement}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={className} aria-label={label} data-testid={testId}>
      {children}
      {badgeElement}
    </button>
  )

  return (
    <Tooltip content={label} side="right" delayDuration={300}>
      {content}
    </Tooltip>
  )
}

export function StandaloneLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const ticketTypes = useAllowedTicketTypes()
  const [userName, setUserName] = useState<string | null>(null)
  const { has } = useAllowedApplications()
  const hasDesktop = has(DESKTOP_APPLICATION)
  const ticketsUnread = useBugReportUnreadCount('mine')

  const activeApp = useMemo(
    () => RAIL_APPS.find(({ id }) => STANDALONE_ROUTES[id] === location.pathname)?.id,
    [location.pathname],
  )
  const ticketsActive = location.pathname === TICKETS_ROUTE

  // Dokumenttitel und Untertitel-Basis folgen der aktiven App.
  const baseTitle = activeApp ? t(`apps.${activeApp}`) : ticketsActive ? t('bugReport.myReports.title') : env.appTitle
  const host = useStandaloneAppHost(baseTitle)

  // Benachrichtigungen öffnen ihr Ziel hier als Route — es gibt keinen
  // Fenstermanager (#1169).
  const navigateTo = useCallback((to: string) => navigate(to), [navigate])
  const notificationHost = useMemo(() => ({ navigate: navigateTo, hasDesktop }), [navigateTo, hasDesktop])

  useEffect(() => {
    let active = true
    authService
      .getCurrentUser()
      .then((user) => {
        if (active) setUserName(user?.name || user?.email || null)
      })
      .catch(() => {
        // Ignorieren: ProtectedRoute kümmert sich um fehlende Anmeldung.
      })
    return () => {
      active = false
    }
  }, [])

  // Ohne Ticket-Rechte gibt es „Meine Tickets“ nicht (#1625): ein Lesezeichen
  // oder Link auf /tickets landet im Chat.
  useEffect(() => {
    if (ticketsActive && !ticketTypes.any) navigate(STANDALONE_ROUTES.communicator2 ?? '/', { replace: true })
  }, [ticketsActive, ticketTypes.any, navigate])

  const handleLogout = async () => {
    try {
      // Sonst sähe der nächste Nutzer in diesem Tab die Rechte des vorigen.
      clearAllowedApplicationsCache()
      clearAllowedTicketTypesCache()
      await authService.logout()
    } catch (error) {
      logError(error as Error, { context: 'StandaloneLayout', action: 'logout' })
    }
    navigate('/')
  }

  return (
    <NotificationHostProvider host={notificationHost}>
    <TooltipProvider delayDuration={300}>
      <div className="flex h-screen overflow-hidden bg-background">
        <nav
          aria-label={t('standalone.navAria')}
          className="flex w-14 shrink-0 flex-col items-center gap-1 bg-background py-3"
        >
          {/* Weiße Kachel unter dem Logo: die Marken-Icons sind mehrfarbig und
              für hellen Grund gezeichnet — auf der Rail-Fläche (und erst recht
              im dunklen Theme) verlören sie sonst ihre Kontur. */}
          <div className="mb-2 grid h-9 w-9 place-items-center rounded-xl bg-white shadow-sm">
            <img
              src={env.appBranding ? `/assets/branding/${env.appBranding}/favicon-32x32.png` : '/assets/favicon-32x32.png'}
              alt=""
              className="h-6 w-6"
            />
          </div>

          {RAIL_APPS.map(({ id, Icon }) => (
            <RailButton
              key={id}
              label={t(`apps.${id}`)}
              to={STANDALONE_ROUTES[id]}
              active={activeApp === id}
            >
              <Icon className="h-5 w-5" />
            </RailButton>
          ))}

          {/* Die eigenen Meldungen und die Antworten des Teams — ohne Desktop
              gibt es dafür sonst keinen Ort (#1169). Fehlt wie „Ticket
              erstellen“, wenn die Rollen beide Ticket-Arten abgeschaltet haben (#1625). */}
          {ticketTypes.any && (
            <RailButton
              label={
                ticketsUnread > 0
                  ? t('tickets.rail.labelUnread', { count: ticketsUnread })
                  : t('tickets.rail.label')
              }
              to={TICKETS_ROUTE}
              active={ticketsActive}
              badge={ticketsUnread}
              testId="standalone-rail-tickets"
            >
              <Ticket className="h-5 w-5" />
            </RailButton>
          )}

          <div className="mt-auto flex flex-col items-center gap-1">
            <NotificationBell anchor="rail-left" />

            {/* „Ticket erstellen“: im Desktop lebt der Einstieg nur noch im Spotlight-Overlay
                (Cmd/Ctrl+K), das es hier nicht gibt — die Rail ist der einzige
                Einstiegspunkt im Standalone-Betrieb. Fehlt, wenn die Rollen
                beide Ticket-Arten abgeschaltet haben (#1544). */}
            {ticketTypes.any && (
              <RailButton
                label={t('ticketPanel.title')}
                onClick={() => window.dispatchEvent(new Event(OPEN_BUG_REPORT_EVENT))}
                testId="standalone-rail-create-ticket"
              >
                <TicketPlus className="h-5 w-5" />
              </RailButton>
            )}

            <RailButton
              label={t('standalone.settings.title')}
              to="/settings"
              active={location.pathname === '/settings'}
            >
              <Settings className="h-5 w-5" />
            </RailButton>

            {/* Nur für Nutzer, die den Desktop überhaupt betreten dürfen —
                für reine PDB-Nutzer wäre das ein Link ins Leere (#504). */}
            {hasDesktop && (
              <RailButton label={t('standalone.toDesktop')} to="/desktop">
                <LayoutGrid className="h-5 w-5" />
              </RailButton>
            )}

            <RailButton label={t('common.logout')} onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </RailButton>

            {/* Reine Anzeige, wer angemeldet ist — kein Knopf: Abmelden und
                Desktop sind eigene Einträge, ein klickbarer Avatar hätte kein
                Ziel mehr. */}
            <Tooltip content={userName ?? t('auth.guest')} side="right" delayDuration={300}>
              <div
                className="mt-1 grid h-9 w-9 select-none place-items-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
                aria-label={userName ?? t('auth.guest')}
              >
                {initialsOf(userName ?? t('auth.guest'))}
              </div>
            </Tooltip>
          </div>
        </nav>

        <main className="min-w-0 flex-1 overflow-hidden">
          <AppHostProvider host={host}>
            <Outlet />
          </AppHostProvider>
        </main>

        {/* "Ticket erstellen" (#1170), beside the rail; the rail button and
            "Bug melden" in "Meine Tickets" open it by event. */}
        <StandaloneTicketPanel />
      </div>
    </TooltipProvider>
    </NotificationHostProvider>
  )
}
